import { Component, OnInit } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, debounceTime, takeUntil } from "rxjs";
import {
	ConfirmationDialog,
	ConfirmationDialogModel,
} from "src/app/share/dialog/confirmation-dialog/confirmation.dialog";
import { AlertService } from "src/app/share/service/alert.service";
import { LoadingService } from "src/app/share/service/loading.service";
import { PermissionService } from "src/app/share/service/permission.service";
import {
	FUNCTION_COMPANY_SETUP_CREATE,
	FUNCTION_COMPANY_SETUP_DEACTIVATE,
	FUNCTION_COMPANY_SETUP_LISTING,
	FUNCTION_COMPANY_SETUP_UPDATE,
} from "../share/constants/access.constants";
import { DismissDialogComponent } from "../share/dialog/dismiss-dialog/dismiss-dialog.component";
import { CompanyDataService } from "./_services/company.data.service";
import {
	BankSelectionInterface,
	CompanyListingInterface,
	CompanyListingInterfaceData,
	NewCompanyPayloadInterface,
	UpdateCompanyInterface,
	VehicleTypeAccessSelectionInterface,
} from "./_utils/company.interface";
import { CreateUpdateCompanyDialogComponent } from "./ui/components/create-company-dialog/create-update-company-dialog.component";

const statusMapping = {
	all: ["0", "1"],
	active: ["1"],
	inActive: ["0"],
};

@Component({
	selector: "app-companies",
	templateUrl: "./companies.component.html",
	styleUrls: ["./companies.component.scss"],
})
export class CompaniesComponent implements OnInit {
	displayedColumns = [
		"createdDate",
		"createdBy",
		"name",
		"ssmid",
		"paymentMethod",
		"status",
		"action",
	];
	list: CompanyListingInterfaceData[] = [];
	isLoading: boolean = false;

	searchText: string = "";
	searchChange: Subject<void> = new Subject<void>();
	length = 0;
	sortIndex: number = 0;
	sortDirection: "asc" | "desc" = "desc";
	limit: number = 50;
	offset: number = 1;
	partnerCode: string = "ps";
	selectedStatus = "all";

	functionalAccess = {
		createCompany: FUNCTION_COMPANY_SETUP_CREATE,
		updateCompany: FUNCTION_COMPANY_SETUP_UPDATE,
		deactivateCompany: FUNCTION_COMPANY_SETUP_DEACTIVATE,
		listing: FUNCTION_COMPANY_SETUP_LISTING,
	};
	functionalAccessList: string[] = [];

	private unsubscribeManually$ = new Subject<void>();

	constructor(
		public loadingService: LoadingService,
		private bottomSheet: MatBottomSheet,
		private router: Router,
		private alertService: AlertService,
		private translateService: TranslateService,
		private permissionService: PermissionService,
		private companyDataService: CompanyDataService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		if (this.router.url.includes("carsome")) {
			this.partnerCode = "carsome";
			localStorage.removeItem("partner");
		}
		this.getData();

		this.searchChange.pipe(debounceTime(500)).subscribe(() => {
			this.filter();
		});
		this.functionalAccessList =
			this.permissionService.getFunctionalAccessList();
	}

	ngOnDestroy() {
		this.unsubscribeManually$.next();
		this.unsubscribeManually$.complete();
	}

	public isAccessAllowed(type: string) {
		return this.functionalAccessList.includes(type);
	}

	private async getData(): Promise<void> {
		this.isLoading = true;
		this.list = [];
		this.length = 0;
		this.companyDataService
			.getListing(
				this.limit,
				this.offset,
				this.searchText,
				this.sortIndex,
				this.sortDirection,
				statusMapping[this.selectedStatus],
			)
			.pipe(takeUntil(this.unsubscribeManually$))
			.subscribe({
				next: (x: CompanyListingInterface) => {
					this.isLoading = false;
					this.list = x.data;
					this.length = x.count;
				},
				error: (err) => {
					this.isLoading = false;
					this.alertService.openSnackBar(err.error);
				},
			});
	}

	private getBankSelection(): Promise<any> {
		this.loadingService.loadingOn(true);
		return new Promise<any>((resolve, reject) => {
			this.companyDataService
				.getBankSelection()
				.pipe(takeUntil(this.unsubscribeManually$))
				.subscribe({
					next: (x: BankSelectionInterface[]) => {
						this.loadingService.loadingOff(true);
						resolve(x);
						return x;
					},
					error: (err) => {
						this.loadingService.loadingOff(true);
						this.alertService.openSnackBar(err.error);
						reject(err);
					},
				});
		});
	}

	private getVehicleTypeAccessSelection(): Promise<any> {
		this.loadingService.loadingOn(true);
		return new Promise<any>((resolve, reject) => {
			this.companyDataService
				.getVehicleTypeAccessSelection()
				.pipe(takeUntil(this.unsubscribeManually$))
				.subscribe({
					next: (x: any[]) => {
						this.loadingService.loadingOff(true);
						resolve(x);
						return x;
					},
					error: (err) => {
						this.loadingService.loadingOff(true);
						this.alertService.openSnackBar(err.error);
						reject(err);
					},
				});
		});
	}

	private createCompany(reqObj: NewCompanyPayloadInterface): void {
		this.loadingService.loadingOn(true);
		this.companyDataService
			.createCompany(reqObj)
			.pipe(takeUntil(this.unsubscribeManually$))
			.subscribe({
				next: () => {
					this.getData();
					this.loadingService.loadingOff(true);
				},
				error: (err) => {
					this.loadingService.loadingOff(true);
					this.alertService.openSnackBar(err.error);
				},
			});
	}

	private updateCompany(reqObj: UpdateCompanyInterface, id: string): void {
		this.loadingService.loadingOn(true);
		this.companyDataService
			.updateCompany(reqObj, id)
			.pipe(takeUntil(this.unsubscribeManually$))
			.subscribe({
				next: () => {
					this.alertService.openSnackBar(
						this.translateService.instant("company.companyDetailsUpdated"),
					);
					this.getData();
					this.loadingService.loadingOff(true);
				},
				error: (err) => {
					this.loadingService.loadingOff(true);
					this.alertService.openSnackBar(err.error);
				},
			});
	}

	public deactivateCompany(userData: any): void {
		let dialogData: ConfirmationDialogModel = {
			title: this.translateService.instant("company.deactivate"),
			content: this.translateService.instant("company.proceedToDeactivate"),
			okBtnText: this.translateService.instant("company.deactivate"),
			cancelBtnText: this.translateService.instant("common.cancel"),
		};

		let dialog = this.dialog.open(ConfirmationDialog, { data: dialogData });

		dialog.afterClosed().subscribe((x) => {
			if (x) {
				this.loadingService.loadingOn(true);
				this.companyDataService
					.deactivateCompany(userData.id)
					.pipe(takeUntil(this.unsubscribeManually$))
					.subscribe({
						next: (x) => {
							this.loadingService.loadingOff(true);
							this.alertService.openSnackBar(
								this.translateService.instant("company.companyDeactivated"),
							);
							this.getData();
						},
						error: (err) => {
							if (err.status === 500 && err.error) {
								this.openDeactivationFailDialog(err.error);
							} else {
								this.alertService.openSnackBar(
									this.translateService.instant(
										"company.companyDeactivatedFail",
									),
								);
							}
							this.loadingService.loadingOff(true);
						},
					});
			}
		});
	}

	public reactivateCompany(id: string): void {
		this.loadingService.loadingOn(true);
		this.companyDataService
			.activateCompany(id)
			.pipe(takeUntil(this.unsubscribeManually$))
			.subscribe({
				next: (x) => {
					this.loadingService.loadingOff(true);
					this.alertService.openSnackBar(
						this.translateService.instant("company.companyReactivated"),
					);
					this.getData();
				},
				error: (err) => {
					this.alertService.openSnackBar(
						this.translateService.instant(
							"company.companyReactivatedFail",
						),
					);
					this.loadingService.loadingOff(true);
				},
			});
	}

	public searchChanged(): void {
		this.searchChange.next();
	}

	public resetTextFilter(): void {
		event.stopImmediatePropagation();
		this.searchText = "";
		this.filter();
	}

	public resetStatusFilter(): void {
		event.stopImmediatePropagation();
		this.selectedStatus = "all";
		this.filter();
	}

	private filter(): void {
		this.offset = 1;
		this.getData();
	}

	public setStatus(status): void {
		this.selectedStatus = status;
		this.filter();
	}

	public async openCreateCompanyDialog() {
		const bankSelectionList = await this.getBankSelection();
		const vehicleTypeAccessList = await this.getVehicleTypeAccessSelection();
		let dialog = this.bottomSheet.open(CreateUpdateCompanyDialogComponent, {
			backdropClass: "sidenav-container",
			disableClose: true,
			data: {
				bankSelectionList: bankSelectionList,
				vehicleTypeAccessList: vehicleTypeAccessList,
				updateCompany: false,
			},
		});

		dialog.afterDismissed().subscribe((reqObj) => {
			if (reqObj) {
				this.createCompany(reqObj);
			}
		});
	}

	public async openDeactivationFailDialog(errMsg: string) {
		this.dialog.open(DismissDialogComponent, {
			disableClose: true,
			data: {
				title: this.translateService.instant(
					"company.unableToDeactivateCompany",
				),
				content: errMsg,
			},
		});
	}

	public async openUpdateCompanyDialog(data: any) {
		const bankSelectionList = await this.getBankSelection();
		const vehicleTypeAccessList = await this.getVehicleTypeAccessSelection();
		let dialog = this.bottomSheet.open(CreateUpdateCompanyDialogComponent, {
			backdropClass: "sidenav-container",
			disableClose: true,
			data: {
				bankSelectionList: bankSelectionList,
				vehicleTypeAccessList: vehicleTypeAccessList,
				updateCompany: true,
				data: data,
			},
		});

		dialog.afterDismissed().subscribe((reqObj) => {
			if (reqObj) {
				const obj: UpdateCompanyInterface = {
					name: reqObj.name,
					address: reqObj.address,
					parentCompanyName: reqObj.parentCompanyName,
					bankId: reqObj.bankId,
					bankAccNo: reqObj.bankAccNo,
					accHolderName: reqObj.accHolderName,
					ssmid: reqObj.ssmid,
					vehicleTypeAccess: reqObj.vehicleTypeAccess
				};
				this.updateCompany(obj, data.id);
			}
		});
	}

	public sortChange(sortState: Sort): void {
		this.sortDirection = null;
		this.sortIndex = -1;
		if (sortState.direction) {
			this.sortDirection = sortState.direction;
			this.sortIndex = this.displayedColumns.findIndex(
				(x) => x == sortState.active,
			);
		}
		if (!this.list) return;

		this.getData();
	}

	public pageChange(e: PageEvent): void {
		this.offset = e.pageIndex + 1;
		this.limit = e.pageSize;
		this.getData();
	}
}
